import { RichText } from "@/components/RichText";
import React, { useEffect, useState } from "react";
import { TopBar } from "@/components/TopBar";
import { ProductCard } from "@/components/ProductCard";
import { EmptyState } from "@/components/EmptyState";
import { useI18n } from "@/i18n";
import { useRouter } from "@/router/Router";
import { useBackButton } from "@/lib/hooks";
import { fetchPost, mediaUrl } from "@/lib/api";
import type { Post, Product } from "@/types";

/** One news item / article / promotion — also used for the legal pages (offer, privacy). */
export function PostPage({ slug }: { slug: string }) {
  const { t, locale } = useI18n();
  const { goBack } = useRouter();
  const [data, setData] = useState<{ post: Post; products: Product[] } | null | undefined>(undefined);

  useBackButton(goBack);

  useEffect(() => {
    setData(undefined);
    fetchPost(slug)
      .then((d) => setData(d ?? null))
      .catch(() => setData(null));
  }, [slug, locale]);

  if (data === undefined) {
    return (
      <>
        <TopBar title="" onBack={goBack} />
        <main className="page page--no-nav">{t("common.loading")}</main>
      </>
    );
  }

  if (data === null) {
    const isLegal = slug === "offer" || slug === "privacy";
    return (
      <>
        <TopBar title="" onBack={goBack} />
        <main className="page page--no-nav">
          <EmptyState title={isLegal ? t("posts.docPending") : t("posts.empty")} />
        </main>
      </>
    );
  }

  const { post, products } = data;
  const cover = mediaUrl(post.coverImage);

  return (
    <>
      <TopBar title={post.title} onBack={goBack} />
      <main className="page page--no-nav">
        {cover && <img src={cover} alt="" className="post-cover" />}
        <h1 style={{ fontFamily: "var(--font-display)", fontSize: 22, fontWeight: 400, margin: "0 0 8px" }}>{post.title}</h1>
        {post.type !== "page" && post.publishedAt && (
          <div style={{ fontSize: 12.5, color: "var(--color-muted)", marginBottom: "var(--space-4)" }}>
            {new Intl.DateTimeFormat(locale === "en" ? "en-GB" : "ru-RU", { day: "numeric", month: "long", year: "numeric" }).format(new Date(post.publishedAt))}
          </div>
        )}
        {post.type === "page" && post.updatedAt && (
          <div style={{ fontSize: 12.5, color: "var(--color-muted)", marginBottom: "var(--space-4)" }}>
            {t("posts.revision", {
              date: new Intl.DateTimeFormat(locale === "en" ? "en-GB" : "ru-RU", { day: "numeric", month: "long", year: "numeric" }).format(new Date(post.updatedAt)),
            })}
          </div>
        )}
        {post.excerpt && <p className="post-lead">{post.excerpt}</p>}
        <RichText text={post.body} />

        {products.length > 0 && (
          <>
            <div className="section-heading" style={{ marginTop: "var(--space-5)" }}>
              <span className="diamond" />
              {t("posts.productsTitle")}
            </div>
            <div className="rail">
              {products.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          </>
        )}
      </main>
    </>
  );
}
